import type { AiActionDTO } from '@shared/api/aiActionsApi'
import { sessionKey } from './schedule'
import type { AiAction, AiActionChannel, AiActionStatus } from './types'

/**
 * Массовые действия и удаления Штаба (S14): разбор ответа API и тексты состояния. Без
 * интерфейса - этим же пользуются экран S14 и карточка в прежнем чате.
 * Подписи без рода, только дефис.
 */

function arr<T>(v: T[] | null | undefined): T[] {
  return Array.isArray(v) ? v : []
}

/** Действие API в форме экрана. Пустые списки Go отдаёт как null. */
export function toAiAction(d: AiActionDTO): AiAction {
  return {
    id: d.id,
    tool: d.tool,
    status: d.status,
    errorCode: d.error_code || undefined,
    title: d.title,
    summary: d.summary ?? '',
    confirmLabel: d.confirm_label || 'Подтвердить',
    items: arr(d.items),
    target: d.target
      ? {
          kind: d.target.kind,
          id: d.target.id,
          label: d.target.label,
          date: d.target.date,
          startTime: d.target.start_time,
          yearMonth: d.target.year_month,
        }
      : undefined,
    note: d.note || undefined,
    danger: !!d.danger,
    result: d.result
      ? { summary: d.result.summary ?? '', done: arr(d.result.done), failed: arr(d.result.failed) }
      : undefined,
    resolvedChannel: d.resolved_channel || undefined,
    createdAt: d.created_at,
    expiresAt: d.expires_at,
    resolvedAt: d.resolved_at || undefined,
  }
}

export const STATUS_LABEL: Record<AiActionStatus, string> = {
  pending: 'Пока не выполнено',
  executing: 'Выполняется',
  executed: 'Выполнено',
  cancelled: 'Отменено',
  expired: 'Срок истёк',
  failed: 'Не выполнено',
}

export const CHANNEL_LABEL: Record<AiActionChannel, string> = {
  web: 'в приложении',
  telegram: 'в Telegram',
  max: 'в MAX',
}

function where(a: AiAction): string {
  return a.resolvedChannel ? ` ${CHANNEL_LABEL[a.resolvedChannel]}` : ''
}

function timeOf(iso: string): string {
  const d = new Date(iso)
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
}

/**
 * Понятная причина состояния. decidedHere - это нажатие только что исполнило
 * или отменило действие; replayed - нажатие ничего не изменило.
 */
export function aiActionExplanation(a: AiAction, opts: { decidedHere?: boolean; replayed?: boolean } = {}): string {
  switch (a.status) {
    case 'pending':
      return `Ничего не изменено, пока вы не подтвердите. Подтвердить можно до ${timeOf(a.expiresAt)}.`
    case 'executing':
      return 'Действие выполняется. Подтверждать ещё раз не нужно.'
    case 'executed':
      if (opts.replayed) return `Уже выполнено раньше${where(a)}. Повторно не выполняется.`
      if (opts.decidedHere) return 'Готово.'
      return `Подтверждено${where(a)}.`
    case 'cancelled':
      if (opts.decidedHere) return 'Отменено. Ничего не изменено.'
      return `Отменено${where(a)}. Ничего не изменено.`
    case 'expired':
      return 'Срок подтверждения истёк. Ничего не изменено. Попросите Штаб подготовить действие заново.'
    case 'failed':
      switch (a.errorCode) {
        case 'stale':
          return 'Данные изменились после подготовки. Ничего не изменено. Попросите Штаб подготовить действие заново.'
        case 'forbidden':
          return 'Нет доступа к клубу или части объектов: права изменились. Ничего не изменено.'
        case 'interrupted':
          return 'Выполнение прервалось. Часть изменений могла пройти - проверьте результат, прежде чем повторять.'
      }
      return a.result?.summary ? `Не удалось выполнить. ${a.result.summary}` : 'Не удалось выполнить.'
  }
}

/** Куда ведёт «Открыть» после исполнения: оплаты группы или занятие. */
export function aiActionLink(a: AiAction): { to: string; label: string } | null {
  const t = a.target
  if (!t) return null
  if (t.kind === 'group_payments') {
    const m = t.yearMonth ? `&m=${t.yearMonth}` : ''
    return { to: `/groups/${t.id}?tab=payments${m}`, label: `Открыть оплаты: ${t.label}` }
  }
  if (t.kind === 'group_schedule') {
    return { to: `/groups/${t.id}?tab=schedule`, label: `Открыть расписание: ${t.label}` }
  }
  if (t.kind === 'session' && t.date) {
    return { to: `/sessions/${sessionKey(t.id, t.date, t.startTime ?? '')}`, label: `Открыть занятие: ${t.label}` }
  }
  return null
}

